import {
  isCurrencyMappedToCalendar,
  returnCalendarForCurrency,
} from "@/lib/businessRules/calendarBusinessRule";
import { findBondSecurityByIsin } from "@/store/bondSecuritiesStoreACTIONS";
import { findFutureSecurityBySymbol } from "@/store/futureSecuritiesStoreACTIONS";
import {
  IBondTicket,
  IBondOptionTicket,
  IFutureTicket,
  IRepoTicket,
  ISwapLegTicket,
  ISwapTicket,
  ISwaptionTicket,
  TicketInstrument,
} from "@/store/tradeFromScratchStore";

// LQC-2985 - which calendars a ticket's dates are checked against. Unmapped currencies
// check nothing: the GBLO fallback would flag London holidays on, say, an IDR date.

export function returnCurrencyHolidayCalendars(
  currency: string | null | undefined,
): string[] {
  return isCurrencyMappedToCalendar(currency) ? [returnCalendarForCurrency(currency)] : [];
}

// Each leg settles on its own calendar; a date is flagged if either leg is closed.
export function returnLegHolidayCalendars(legs: {
  leg1: Pick<ISwapLegTicket, "calendar">;
  leg2: Pick<ISwapLegTicket, "calendar">;
}): string[] {
  return [...new Set([legs.leg1.calendar, legs.leg2.calendar].filter(Boolean))];
}

// Settlement (the Effective Date) follows the security's currency, an option's payment
// currency, or a swap's legs. Shared by the Leg Creation tickets and the draft rows.
export function returnSettlementHolidayCalendars(
  instrument: TicketInstrument,
  ticket: unknown,
): string[] {
  if (!ticket) return [];
  switch (instrument) {
    case TicketInstrument.Bond:
      return returnCurrencyHolidayCalendars(
        findBondSecurityByIsin((ticket as IBondTicket).isin)?.currency,
      );
    case TicketInstrument.Repo:
      return returnCurrencyHolidayCalendars(
        findBondSecurityByIsin((ticket as IRepoTicket).isin)?.currency,
      );
    case TicketInstrument.Future:
      return returnCurrencyHolidayCalendars(
        findFutureSecurityBySymbol((ticket as IFutureTicket).bloombergSymbol)?.currency,
      );
    case TicketInstrument.BondOption:
      return returnCurrencyHolidayCalendars((ticket as IBondOptionTicket).currencyPayment);
    case TicketInstrument.Swaption:
      return returnCurrencyHolidayCalendars((ticket as ISwaptionTicket).currencyPayment);
    case TicketInstrument.Swap:
      return returnLegHolidayCalendars(ticket as ISwapTicket);
    default:
      return [];
  }
}
